import { useMemo } from 'react';
import { selector, useRecoilState, useRecoilValue } from 'recoil';
import {
  workspaceScoresState,
  workspacesLoadedState,
  workspacesState,
  workspaceUserMembershipsState
} from '../../store/workspacesAtoms';
import { WorkspaceUserMembershipStatus } from '../../types/common';
import { Workspace } from '../../types/files';

const workspaceUserMembershipsSelector = selector({
  key: 'workspaceUserMembershipsSelector',
  get: ({ get }) => {
    const workspaceUserMemberships = get(workspaceUserMembershipsState);

    const acceptedWorkspaces = workspaceUserMemberships
      ?.filter(membership => membership.status === WorkspaceUserMembershipStatus.ACCEPTED)
      .map(workspace => ({
        id: workspace.workspaceId,
        name: workspace.name,
        group: workspace.workspaceGroup,
        createdAt: workspace.createdAt,
        updatedAt: workspace.updatedAt,
        isPublic: true,
        isShared: true,
      })) ?? [];

    const pendingWorkspaces = workspaceUserMemberships
      ?.filter(membership => membership.status === WorkspaceUserMembershipStatus.PENDING)
      .map(workspace => ({
        id: workspace.workspaceId,
        name: workspace.name,
        group: workspace.workspaceGroup,
        createdAt: workspace.createdAt,
        updatedAt: workspace.updatedAt,
        isPublic: true,
        isShared: true,
      })) ?? [];

    return {
      acceptedWorkspaces: acceptedWorkspaces as unknown as (Workspace & { group?: string })[],
      pendingWorkspaces: pendingWorkspaces as unknown as (Workspace & { group?: string })[],
    };
  },
});

const useWorkspacesContext = () => {
  const [workspacesLoaded, setWorkspacesLoaded] = useRecoilState(workspacesLoadedState);
  const [workspaces, setWorkspaces] = useRecoilState(workspacesState);
  const [workspaceUserMemberships, setWorkspaceUserMemberships] = useRecoilState(workspaceUserMembershipsState);
  const [workspaceScores, setWorkspaceScores] = useRecoilState(workspaceScoresState);
  const membershipsStatusGroups = useRecoilValue(workspaceUserMembershipsSelector);

  const context = useMemo(() => ({
    workspacesLoaded,
    workspaces,
    workspaceUserMemberships,
    workspaceScores,
    membershipsStatusGroups,
    setWorkspacesLoaded,
    setWorkspaces,
    setWorkspaceUserMemberships,
    setWorkspaceScores
  }), [
    workspacesLoaded,
    workspaces,
    workspaceUserMemberships,
    workspaceScores,
    membershipsStatusGroups,
    setWorkspacesLoaded,
    setWorkspaces,
    setWorkspaceUserMemberships,
    setWorkspaceScores
  ]);

  return context;
};

export default useWorkspacesContext;